"use client";

import React from "react";
import { Plus, Leaf, Coffee, Wheat, Info, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
interface MenuItem {
  id: string;
  title: string;
  price: string;
  description: string;
  allergens: string;
  sourcing: string;
}
interface MenuCategory {
  category: string;
  items: MenuItem[];
}
const menuItems: readonly MenuCategory[] = [{
  category: "Structural Restoration",
  items: [{
    id: "p1",
    title: "Brick Repointing",
    price: "Contact",
    description: "Professional removal of deteriorated mortar and replacement with high-quality, color-matched mortar to restore structural integrity.",
    allergens: "Dust Protection, Site Safety, Material Matching",
    sourcing: "Premium lime-based mortars and specialized pigments to match historic or existing masonry structures."
  }, {
    id: "p2",
    title: "Cracked Brick Replacement",
    price: "Quote",
    description: "Careful extraction of damaged or spalling bricks and replacement with matching units to prevent water ingress and further decay.",
    allergens: "Structural Assessment, Color Matching, Weather Sealing",
    sourcing: "Sourced from local brickyards and reclaimed suppliers to ensure a seamless visual match with original materials."
  }, {
    id: "p3",
    title: "Foundation Parging",
    price: "Custom",
    description: "Application of a fresh coat of mortar to foundation walls to improve aesthetics and provide an extra layer of protection against the elements.",
    allergens: "Surface Prep, Moisture Barrier, Finish Texture",
    sourcing: "High-strength cementitious mixes designed for durability in Ontario's freeze-thaw cycles."
  }]
}, {
  category: "Specialized Repairs",
  items: [{
    id: "d1",
    title: "Chimney Rebuilding",
    price: "Expert",
    description: "Complete or partial reconstruction of chimneys, including new flashing and caps to ensure safe operation and longevity.",
    allergens: "Height Safety, Flashing Integrity, Code Compliance",
    sourcing: "Industrial grade firebricks and weather-resistant masonry units for maximum thermal performance."
  }, {
    id: "d2",
    title: "Stone Veneer Repair",
    price: "Detail",
    description: "Precision repair of natural or manufactured stone veneers, addressing loose stones and failing mortar joints.",
    allergens: "Adhesion Testing, Joint Consistency, Aesthetic Blending",
    sourcing: "Natural limestone, granite, or high-quality manufactured stone from trusted regional quarries."
  }, {
    id: "d3",
    title: "Lintel Replacement",
    price: "Structural",
    description: "Replacement of rusted or failing steel lintels above windows and doors to prevent masonry collapse and cracking.",
    allergens: "Temporary Shoring, Steel Priming, Load Bearing",
    sourcing: "Galvanized steel components and corrosion-resistant coatings for long-term structural stability."
  }]
}] as const;
export default function LaCarteDuMoment() {
  return <section data-section-id="225206" id="restoration-repair" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        <div className="mb-12 md:mb-16 text-center md:text-left">
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-widest uppercase mb-4">
            Restoration & Repair
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-default">
            Expert masonry restoration services designed to preserve the beauty and integrity of your structure using time-tested techniques and matching materials.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 lg:sticky lg:top-24 h-[400px] lg:h-[calc(100vh-12rem)] min-h-[500px] rounded-none overflow-hidden relative shadow-none">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1656752/img/masonry_restoration_detail.png" alt="Expert masonry restoration work" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10 pointer-events-none" />
            
            <div className="absolute top-6 left-6 flex flex-col gap-3">
              <Badge className="bg-primary text-primary-foreground border-none shadow-none px-3 py-1.5 text-sm font-medium rounded-none">
                <Leaf className="w-4 h-4 mr-2" />
                Lasting Solutions
              </Badge>
              <Badge className="bg-secondary text-secondary-foreground border-none shadow-none px-3 py-1.5 text-sm font-medium rounded-none">
                <Coffee className="w-4 h-4 mr-2" />
                Expert Matching
              </Badge>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ScrollArea className="h-full lg:h-[calc(100vh-12rem)] lg:pr-6">
              <div className="flex flex-col gap-12 pb-12">
                {menuItems.map((category, catIndex) => <div key={category.category} data-index={catIndex} className="flex flex-col gap-6">
                    <h3 className="font-default text-2xl font-semibold border-b border-border pb-2 text-foreground/90 tracking-wider uppercase">
                      {category.category}
                    </h3>
                    
                    <div className="flex flex-col gap-4">
                      {category.items.map((item, itemIndex) => <div key={item.id} data-index={itemIndex} className="group relative flex items-center justify-between p-2 -mx-2 rounded-none hover:bg-muted/50 transition-colors">
                          
                          <div className="flex-1 flex items-baseline justify-between pr-4">
                            <span className="font-default text-lg md:text-xl font-medium text-foreground uppercase tracking-wide">
                              {item.title}
                            </span>
                            
                            <div className="flex-1 border-b border-dotted border-muted-foreground/30 mx-4 relative top-[-6px] hidden sm:block" />
                            
                            <span className="font-default text-base md:text-lg font-semibold text-primary">
                              {item.price}
                            </span>
                          </div>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="rounded-full shrink-0 h-8 w-8 bg-background border border-border shadow-none hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all">
                                <Plus className="w-4 h-4" />
                                <span className="sr-only">Détails de {item.title}</span>
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[425px] rounded-none border-border">
                              <DialogHeader>
                                <DialogTitle className="font-default text-2xl uppercase tracking-widest">{item.title}</DialogTitle>
                                <DialogDescription className="font-default text-primary text-lg mt-1 font-semibold">
                                  {item.price} Service
                                </DialogDescription>
                              </DialogHeader>
                              
                              <div className="mt-4 space-y-6">
                                <div>
                                  <p className="text-foreground font-default leading-relaxed">
                                    {item.description}
                                  </p>
                                </div>
                                
                                <div className="space-y-3 bg-muted/30 p-4 rounded-none border border-border/50">
                                  <div className="flex items-start gap-3">
                                    <Wheat className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                                    <div>
                                      <h4 className="font-default text-sm font-semibold text-foreground uppercase tracking-tighter">Key Considerations</h4>
                                      <p className="text-sm text-muted-foreground font-default">{item.allergens}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-start gap-3">
                                    <MapPin className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                                    <div>
                                      <h4 className="font-default text-sm font-semibold text-foreground uppercase tracking-tighter">Methodology</h4>
                                      <p className="text-sm text-muted-foreground font-default">{item.sourcing}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                        </div>)}
                    </div>
                  </div>)}
              </div>
            </ScrollArea>
          </div>

        </div>
      </div>
    </section>;
}